import { FC, Fragment, useMemo, useState } from 'react';

import type { Permission } from '@/api/permissions';
import ChevronDownIcon from '@/assets/svg/ui-elements/chevron-down.svg';
import ChevronRightIcon from '@/assets/svg/ui-elements/chevron-right.svg';
import { Button, Loader, Select, Switch, theme, Typography } from '@/shared/design-system';
import { buildRoleComparison, type GroupCoverage } from '@/utils/role-comparison';

import { formatMemberCount } from '../../role-descriptions';
import { CenteredState } from '../../styled';
import type { CatalogGroup, RoleSummary } from '../../types';
import {
  CompareRoot,
  CompareToolbar,
  CoverageCell,
  CoverageDot,
  GroupToggle,
  Legend,
  LegendItem,
  Matrix,
  MatrixScroller,
  RoleColumnButton,
  RolesPickerSlot,
  ToggleLabel,
  ToolbarControls,
} from './styled';

export const ONLY_DIFFERENCES_LABEL = 'Only differences';
export const ROLES_PICKER_LABEL = 'Roles to compare';
const MIN_COMPARED_ROLES = 2;
const ROLES_SEARCH_THRESHOLD = 8;
export const NO_DIFFERENCES_MESSAGE = 'These roles grant exactly the same permissions.';
export const TOO_FEW_ROLES_MESSAGE = 'Add another role to compare it with.';
export const COMPARE_LOAD_ERROR = "Couldn't load every role's permissions.";
export const COMPARE_HINT =
  'People get everything from all their roles combined. Nothing is ever taken away.';

export type ComparedRoleColumn = {
  role: RoleSummary;
  /** Undefined while the role's detail is still loading. */
  permissions?: ReadonlySet<Permission>;
};

export type RoleCompareProps = {
  groups: readonly CatalogGroup[];
  columns: readonly ComparedRoleColumn[];
  /** True while the permission catalogue the rows come from is still loading. */
  catalogLoading?: boolean;
  /** The catalogue or a role's detail failed to load, so the matrix would wait forever. */
  loadFailed?: boolean;
  onRetry?: () => void;
  onOpenRole: (_roleId: string) => void;
};

const coverageLabel = ({ granted, total, level }: GroupCoverage) =>
  level === 'full' ? `All ${total}` : level === 'none' ? 'None' : `${granted} of ${total}`;

/**
 * Every role side by side. Answers "which role should this person get?", which the one-role
 * view cannot: catalogue groups are rows, roles are columns, and a group expands to its
 * individual permissions.
 */
export const RoleCompare: FC<RoleCompareProps> = ({
  groups,
  columns,
  catalogLoading = false,
  loadFailed = false,
  onRetry,
  onOpenRole,
}) => {
  const [onlyDifferences, setOnlyDifferences] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [hiddenRoleIds, setHiddenRoleIds] = useState<ReadonlySet<string>>(new Set());

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenRoleIds.has(column.role.id)),
    [columns, hiddenRoleIds],
  );
  const selectedRoleIds = visibleColumns.map((column) => column.role.id);
  const isAtMinimum = selectedRoleIds.length <= MIN_COMPARED_ROLES;

  const roleOptions = columns.map(({ role }) => ({
    value: role.id,
    label: role.name,
    supportText: formatMemberCount(role.memberCount),
    disabled: isAtMinimum && !hiddenRoleIds.has(role.id),
  }));

  const changeSelectedRoles = (nextIds: string[]) => {
    if (nextIds.length < MIN_COMPARED_ROLES) return;
    setHiddenRoleIds(
      new Set(columns.map((column) => column.role.id).filter((id) => !nextIds.includes(id))),
    );
  };

  // Without groups there is nothing to compare yet: an empty catalogue must not read as "these
  // roles grant the same permissions".
  const isLoading =
    catalogLoading || !groups.length || visibleColumns.some((column) => !column.permissions);

  const rows = useMemo(
    () =>
      isLoading
        ? []
        : buildRoleComparison(
            groups,
            visibleColumns.map((column) => ({
              id: column.role.id,
              permissions: column.permissions ?? new Set(),
            })),
            { onlyDifferences },
          ),
    [groups, visibleColumns, onlyDifferences, isLoading],
  );

  const toggleGroup = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });

  return (
    <CompareRoot>
      <CompareToolbar>
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {COMPARE_HINT}
        </Typography>
        <ToolbarControls>
          {columns.length > MIN_COMPARED_ROLES && (
            <RolesPickerSlot>
              <Select
                multiple
                size="sm"
                triggerLabel="Roles"
                ariaLabel={ROLES_PICKER_LABEL}
                options={roleOptions}
                value={selectedRoleIds}
                onChange={changeSelectedRoles}
                showSearch={columns.length > ROLES_SEARCH_THRESHOLD}
                popupWidth={280}
              />
            </RolesPickerSlot>
          )}
          <ToggleLabel>
            <Switch
              size="sm"
              checked={onlyDifferences}
              onCheckedChange={setOnlyDifferences}
              ariaLabel={ONLY_DIFFERENCES_LABEL}
            />
            <Typography variant="body3" color={theme.colors.text.primary}>
              {ONLY_DIFFERENCES_LABEL}
            </Typography>
          </ToggleLabel>
        </ToolbarControls>
      </CompareToolbar>

      {isLoading && loadFailed ? (
        <CenteredState>
          <Typography variant="body3" color={theme.colors.text.secondary}>
            {COMPARE_LOAD_ERROR}
          </Typography>
          {onRetry && (
            <Button size="sm" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          )}
        </CenteredState>
      ) : isLoading ? (
        <CenteredState>
          <Loader />
        </CenteredState>
      ) : !rows.length ? (
        <CenteredState>
          <Typography variant="body3" color={theme.colors.text.secondary}>
            {visibleColumns.length < MIN_COMPARED_ROLES
              ? TOO_FEW_ROLES_MESSAGE
              : NO_DIFFERENCES_MESSAGE}
          </Typography>
        </CenteredState>
      ) : (
        <MatrixScroller>
          <Matrix>
            <thead>
              <tr>
                <th scope="col">
                  <Typography variant="body4" color={theme.colors.text.tertiary}>
                    Permission
                  </Typography>
                </th>
                {visibleColumns.map(({ role, permissions }) => (
                  <th key={role.id} scope="col">
                    <RoleColumnButton type="button" onClick={() => onOpenRole(role.id)}>
                      <Typography
                        variant="body3"
                        fontWeight={500}
                        color={theme.colors.text.primary}
                      >
                        {role.name}
                      </Typography>
                      <Typography variant="body4" color={theme.colors.text.tertiary}>
                        {formatMemberCount(role.memberCount)} · {permissions?.size ?? 0}
                      </Typography>
                    </RoleColumnButton>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = expanded.has(row.group.key);

                return (
                  <Fragment key={row.group.key}>
                    <tr data-row="group">
                      <th scope="row">
                        <GroupToggle
                          type="button"
                          aria-expanded={isOpen}
                          onClick={() => toggleGroup(row.group.key)}
                        >
                          {isOpen ? (
                            <ChevronDownIcon aria-hidden />
                          ) : (
                            <ChevronRightIcon aria-hidden />
                          )}
                          <Typography
                            variant="body3"
                            fontWeight={500}
                            color={theme.colors.text.primary}
                          >
                            {row.group.label}
                          </Typography>
                          <Typography variant="body4" color={theme.colors.text.tertiary}>
                            {row.group.permissions.length}
                          </Typography>
                        </GroupToggle>
                      </th>
                      {row.cells.map((cell, index) => (
                        <td key={visibleColumns[index].role.id}>
                          <CoverageCell>
                            <CoverageDot $level={cell.level} aria-label={coverageLabel(cell)} />
                            {cell.level === 'partial' && (
                              <Typography variant="body4" color={theme.colors.text.tertiary}>
                                {cell.granted}
                              </Typography>
                            )}
                          </CoverageCell>
                        </td>
                      ))}
                    </tr>

                    {isOpen &&
                      row.permissions.map((permissionRow) => (
                        <tr key={permissionRow.permission.permission} data-row="permission">
                          <th scope="row">
                            <Typography variant="body4" color={theme.colors.text.secondary}>
                              {permissionRow.permission.label}
                            </Typography>
                          </th>
                          {permissionRow.granted.map((granted, index) => (
                            <td key={visibleColumns[index].role.id}>
                              <CoverageCell>
                                <CoverageDot
                                  $level={granted ? 'full' : 'none'}
                                  aria-label={granted ? 'Granted' : 'Not granted'}
                                />
                              </CoverageCell>
                            </td>
                          ))}
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </Matrix>
        </MatrixScroller>
      )}

      <Legend>
        <LegendItem>
          <CoverageDot $level="full" aria-hidden />
          <Typography variant="body4" color={theme.colors.text.tertiary}>
            All
          </Typography>
        </LegendItem>
        <LegendItem>
          <CoverageDot $level="partial" aria-hidden />
          <Typography variant="body4" color={theme.colors.text.tertiary}>
            Some (count shown)
          </Typography>
        </LegendItem>
        <LegendItem>
          <CoverageDot $level="none" aria-hidden />
          <Typography variant="body4" color={theme.colors.text.tertiary}>
            None
          </Typography>
        </LegendItem>
      </Legend>
    </CompareRoot>
  );
};
